import { useState, useEffect } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import DateTimePicker from "@components/ui/selection/DateTimePicker";
import { ordresApi } from "@services/api/ordres";
import { bookingsApi } from "@services/api/bookings";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import type { Ordre } from "types/ordre";

interface SecondaryOrdreModalProps {
  ordre: Ordre;
  onClose: () => void;
}

function formatDateStr(seconds: number): string {
  const d = new Date(seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function SecondaryOrdreModal({ ordre, onClose }: SecondaryOrdreModalProps) {
  const [name, setName] = useState(ordre.name);
  const [receptionSeconds, setReceptionSeconds] = useState<number | null>(null);
  const [restitutionSeconds, setRestitutionSeconds] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [receptionDisabled, setReceptionDisabled] = useState<string[]>([]);
  const [restitutionDisabled, setRestitutionDisabled] = useState<string[]>([]);

  // Fetch availability when reception date changes
  useEffect(() => {
    if (!receptionSeconds || !ordre.org_id) { setReceptionDisabled([]); return; }
    const dateStr = formatDateStr(receptionSeconds);
    bookingsApi.getAvailability(dateStr, "Mécanique", ordre.org_id)
      .then((res) => setReceptionDisabled(res.unavailable_slots))
      .catch(() => setReceptionDisabled([]));
  }, [receptionSeconds ? formatDateStr(receptionSeconds) : null, ordre.org_id]);

  // Fetch availability when restitution date changes
  useEffect(() => {
    if (!restitutionSeconds || !ordre.org_id) { setRestitutionDisabled([]); return; }
    const dateStr = formatDateStr(restitutionSeconds);
    bookingsApi.getAvailability(dateStr, "Mécanique", ordre.org_id)
      .then((res) => setRestitutionDisabled(res.unavailable_slots))
      .catch(() => setRestitutionDisabled([]));
  }, [restitutionSeconds ? formatDateStr(restitutionSeconds) : null, ordre.org_id]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const num = crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();
      const newOrdre = await ordresApi.create({
        org_id: ordre.org_id,
        num,
        name,
        status: "a_preparer",
        tags: [],
        dossier_id: ordre.dossier_id,
        vehicle_id: ordre.vehicle_id,
        contact_ids: ordre.contact_ids,
        immatriculation: ordre.immatriculation,
        modele: ordre.modele,
        interventions: [],
        documents: [],
        created_at: { seconds: Math.floor(Date.now() / 1000) },
        ...(receptionSeconds ? { reception_date: { seconds: receptionSeconds } } : {}),
        ...(restitutionSeconds ? { restitution: { seconds: restitutionSeconds } } : {}),
      });
      useOrdreStore.getState().addOrdre(newOrdre);
      showNotification({ type: "success", message: "RDV secondaire créé" });
      onClose();
      navigateTo({ route: `/ordre/${newOrdre.id}` });
    } catch (e) {
      console.error("Error creating secondary ordre:", e);
      showNotification({ type: "error", message: "Erreur lors de la création du RDV secondaire" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un RDV secondaire"
      startIcon={<Icon1 name="plus" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField label="Nom" value={name} onChange={setName} />
        <DateTimePicker
          label="Date réception"
          seconds={receptionSeconds}
          onChange={setReceptionSeconds}
          disabledSlots={receptionDisabled}
        />
        <DateTimePicker
          label="Date restitution"
          seconds={restitutionSeconds}
          onChange={setRestitutionSeconds}
          disabledSlots={restitutionDisabled}
        />

        {ordre.vehicle && (
          <div className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200">
            <Icon1 name="car-reception" size={16} color="#737373" />
            <span className="text-h3 text-neutral-600">
              {ordre.vehicle.plate} — {ordre.vehicle.marque} {ordre.vehicle.modele}
            </span>
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={saving ? "Création..." : "Créer le RDV"}
          onClick={handleCreate}
          disabled={saving || !name.trim()}
        />
      </div>
    </BaseModal>
  );
}
